/*
=========================================================
SHOP THE ROOM
Version 2.1
Desktop hotspots + mobile Get the Look drawer
+ full room viewer
=========================================================
*/

class ShopTheRoom {
  constructor(section) {
    this.section = section;

    this.hotspots = section.querySelectorAll('.shop-room__hotspot');
    this.popup = section.querySelector('.shop-room-popup');

    this.drawer = section.querySelector('.shop-room-drawer');
    this.drawerBackdrop = section.querySelector('.shop-room-drawer-backdrop');
    this.drawerTrigger = section.querySelector('.shop-room__drawer-trigger');
    this.drawerClose = section.querySelector('.shop-room-drawer__close');

    this.fullRoomTrigger = section.querySelector(
      '[data-shop-room-full-room-trigger]'
    );

    this.fullRoomViewer = section.querySelector(
      '[data-shop-room-full-room-viewer]'
    );

    this.fullRoomCloseButtons = section.querySelectorAll(
      '[data-shop-room-full-room-close]'
    );

    this.fullRoomViewport = section.querySelector(
      '.shop-room-room-viewer__viewport'
    );

    this.activeHotspot = null;
    this.lastFocusedElement = null;
    this.lastFullRoomFocusedElement = null;

    if (this.popup) {
      this.popupImage = this.popup.querySelector('.shop-room-popup__image');

      this.popupImageWrapper = this.popup.querySelector(
        '.shop-room-popup__image-wrapper'
      );

      this.popupEyebrow = this.popup.querySelector(
        '.shop-room-popup__eyebrow'
      );

      this.popupTitle = this.popup.querySelector(
        '.shop-room-popup__title'
      );

      this.popupPrice = this.popup.querySelector(
        '.shop-room-popup__price'
      );

      this.popupButton = this.popup.querySelector(
        '.shop-room-popup__button'
      );
    }

    this.init();
  }

  init() {
    this.initHotspots();
    this.initDrawer();
    this.initFullRoomViewer();

    this.section.addEventListener('mouseleave', () => {
      if (window.innerWidth > 767) {
        this.hidePopup();
      }
    });

    document.addEventListener('click', (event) => {
      if (!this.section.contains(event.target)) {
        this.hidePopup();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      if (
        this.fullRoomViewer &&
        this.fullRoomViewer.classList.contains('is-open')
      ) {
        this.closeFullRoomViewer();
        return;
      }

      if (
        this.drawer &&
        this.drawer.classList.contains('is-open')
      ) {
        this.closeDrawer();
        return;
      }

      const hotspotToBlur = this.activeHotspot;

      this.hidePopup();

      if (hotspotToBlur) {
        hotspotToBlur.blur();
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > 767) {
        this.closeDrawer(false);
        this.closeFullRoomViewer(false);
      }

      if (this.activeHotspot) {
        this.positionPopup(this.activeHotspot);
      }
    });
  }

  initHotspots() {
    this.hotspots.forEach((hotspot) => {
      hotspot.addEventListener('mouseenter', () => {
        if (window.innerWidth > 767) {
          this.showPopup(hotspot);
        }
      });

      hotspot.addEventListener('focus', () => {
        if (
          window.innerWidth > 767 ||
          !this.section.classList.contains(
            'shop-room--mobile-drawer'
          )
        ) {
          this.showPopup(hotspot);
        }
      });

      hotspot.addEventListener('click', (event) => {
        event.preventDefault();

        if (
          window.innerWidth <= 767 &&
          this.section.classList.contains(
            'shop-room--mobile-drawer'
          )
        ) {
          this.openDrawer();
          return;
        }

        this.showPopup(hotspot);
      });
    });
  }

  initDrawer() {
    if (!this.drawer || !this.drawerTrigger) return;

    this.drawerTrigger.addEventListener('click', () => {
      this.openDrawer();
    });

    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', () => {
        this.closeDrawer();
      });
    }

    if (this.drawerBackdrop) {
      this.drawerBackdrop.addEventListener('click', () => {
        this.closeDrawer();
      });
    }
  }

  initFullRoomViewer() {
    if (!this.fullRoomTrigger || !this.fullRoomViewer) return;

    this.fullRoomTrigger.addEventListener('click', () => {
      this.openFullRoomViewer();
    });

    this.fullRoomCloseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.closeFullRoomViewer();
      });
    });
  }

  openDrawer() {
    if (!this.drawer) return;

    this.lastFocusedElement = document.activeElement;

    this.hidePopup();

    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');

    if (this.drawerBackdrop) {
      this.drawerBackdrop.classList.add('is-open');
      this.drawerBackdrop.setAttribute('aria-hidden', 'false');
    }

    if (this.drawerTrigger) {
      this.drawerTrigger.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    document.body.classList.add('shop-room-drawer-open');

    window.requestAnimationFrame(() => {
      if (this.drawerClose) {
        this.drawerClose.focus();
      }
    });
  }

  closeDrawer(restoreFocus = true) {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');

    if (this.drawerBackdrop) {
      this.drawerBackdrop.classList.remove('is-open');
      this.drawerBackdrop.setAttribute('aria-hidden', 'true');
    }

    if (this.drawerTrigger) {
      this.drawerTrigger.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    document.body.classList.remove('shop-room-drawer-open');

    if (
      restoreFocus &&
      this.lastFocusedElement &&
      typeof this.lastFocusedElement.focus === 'function'
    ) {
      this.lastFocusedElement.focus();
    }

    this.lastFocusedElement = null;
  }

  openFullRoomViewer() {
    if (!this.fullRoomViewer) return;

    this.lastFullRoomFocusedElement = document.activeElement;

    this.fullRoomViewer.classList.add('is-open');
    this.fullRoomViewer.setAttribute('aria-hidden', 'false');

    document.body.classList.add(
      'shop-room-room-viewer-open'
    );

    window.requestAnimationFrame(() => {
      if (this.fullRoomViewport) {
        this.fullRoomViewport.scrollLeft =
          (
            this.fullRoomViewport.scrollWidth -
            this.fullRoomViewport.clientWidth
          ) / 2;
      }

      const closeButton = this.fullRoomViewer.querySelector(
        '.shop-room-room-viewer__close'
      );

      if (closeButton) {
        closeButton.focus();
      }
    });
  }

  closeFullRoomViewer(restoreFocus = true) {
    if (!this.fullRoomViewer) return;

    this.fullRoomViewer.classList.remove('is-open');
    this.fullRoomViewer.setAttribute('aria-hidden', 'true');

    document.body.classList.remove(
      'shop-room-room-viewer-open'
    );

    if (
      restoreFocus &&
      this.lastFullRoomFocusedElement &&
      typeof this.lastFullRoomFocusedElement.focus ===
        'function'
    ) {
      this.lastFullRoomFocusedElement.focus();
    }

    this.lastFullRoomFocusedElement = null;
  }

  showPopup(hotspot) {
    if (!this.popup) return;

    const data = hotspot.dataset;

    this.activeHotspot = hotspot;

    this.popupTitle.textContent =
      data.itemTitle || '';

    this.popupButton.textContent =
      data.buttonLabel || 'View';

    this.popupButton.href =
      data.itemUrl || '#';

    this.popupEyebrow.textContent = '';
    this.popupEyebrow.style.display = 'none';

    if (data.itemPrice) {
      this.popupPrice.textContent =
        data.itemPrice;

      this.popupPrice.style.display = '';
    } else {
      this.popupPrice.textContent = '';
      this.popupPrice.style.display = 'none';
    }

    if (data.itemImage) {
      this.popupImage.src =
        data.itemImage;

      this.popupImage.alt =
        data.itemTitle || '';

      this.popupImageWrapper.style.display = '';
    } else {
      this.popupImage.removeAttribute('src');
      this.popupImage.alt = '';
      this.popupImageWrapper.style.display = 'none';
    }

    this.positionPopup(hotspot);

    this.hotspots.forEach((item) => {
      item.classList.remove('is-active');
      item.setAttribute(
        'aria-expanded',
        'false'
      );
    });

    hotspot.classList.add('is-active');
    hotspot.setAttribute(
      'aria-expanded',
      'true'
    );

    this.popup.classList.add('is-active');
    this.popup.setAttribute(
      'aria-hidden',
      'false'
    );
  }

  hidePopup() {
    if (!this.popup) return;

    this.popup.classList.remove('is-active');
    this.popup.setAttribute(
      'aria-hidden',
      'true'
    );

    this.hotspots.forEach((item) => {
      item.classList.remove('is-active');

      item.setAttribute(
        'aria-expanded',
        'false'
      );
    });

    this.activeHotspot = null;
  }

  positionPopup(hotspot) {
    if (
      !this.popup ||
      window.innerWidth <= 767
    ) {
      return;
    }

    const wrapper = this.section.querySelector(
      '.shop-room__image-wrapper'
    );

    if (!wrapper) return;

    const wrapperRect =
      wrapper.getBoundingClientRect();

    const hotspotRect =
      hotspot.getBoundingClientRect();

    const popupWidth =
      this.popup.offsetWidth || 180;

    const popupHeight =
      this.popup.offsetHeight || 260;

    const spacing = 20;

    const hotspotX =
      hotspotRect.left -
      wrapperRect.left +
      hotspotRect.width / 2;

    const hotspotY =
      hotspotRect.top -
      wrapperRect.top +
      hotspotRect.height / 2;

    let preferredPosition =
      hotspot.dataset.cardPosition || 'auto';

    if (preferredPosition === 'auto') {
      preferredPosition =
        this.getAutoPosition(
          hotspotX,
          hotspotY,
          wrapperRect,
          popupWidth,
          popupHeight
        );
    }

    let left =
      hotspotX + spacing;

    let top =
      hotspotY - popupHeight / 2;

    if (preferredPosition === 'left') {
      left =
        hotspotX -
        popupWidth -
        spacing;

      top =
        hotspotY -
        popupHeight / 2;
    }

    if (preferredPosition === 'right') {
      left =
        hotspotX +
        spacing;

      top =
        hotspotY -
        popupHeight / 2;
    }

    if (preferredPosition === 'top') {
      left =
        hotspotX -
        popupWidth / 2;

      top =
        hotspotY -
        popupHeight -
        spacing;
    }

    if (preferredPosition === 'bottom') {
      left =
        hotspotX -
        popupWidth / 2;

      top =
        hotspotY +
        spacing;
    }

    left = Math.max(
      12,
      Math.min(
        left,
        wrapperRect.width -
          popupWidth -
          12
      )
    );

    top = Math.max(
      12,
      Math.min(
        top,
        wrapperRect.height -
          popupHeight -
          12
      )
    );

    this.popup.style.left =
      `${left}px`;

    this.popup.style.top =
      `${top}px`;

    this.popup.dataset.position =
      preferredPosition;
  }

  getAutoPosition(
    hotspotX,
    hotspotY,
    wrapperRect,
    popupWidth,
    popupHeight
  ) {
    const spaceRight =
      wrapperRect.width -
      hotspotX;

    const spaceLeft =
      hotspotX;

    const spaceBelow =
      wrapperRect.height -
      hotspotY;

    const spaceAbove =
      hotspotY;

    if (
      spaceRight >=
      popupWidth + 50
    ) {
      return 'right';
    }

    if (
      spaceLeft >=
      popupWidth + 50
    ) {
      return 'left';
    }

    if (
      spaceBelow >=
      popupHeight + 50
    ) {
      return 'bottom';
    }

    if (
      spaceAbove >=
      popupHeight + 50
    ) {
      return 'top';
    }

    return 'right';
  }
}

function initialiseShopTheRoom(scope = document) {
  scope
    .querySelectorAll('.shop-room')
    .forEach((section) => {
      if (
        section.dataset
          .shopRoomInitialised ===
        'true'
      ) {
        return;
      }

      section.dataset
        .shopRoomInitialised =
        'true';

      new ShopTheRoom(section);
    });
}

document.addEventListener(
  'DOMContentLoaded',
  () => {
    initialiseShopTheRoom();
  }
);

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initialiseShopTheRoom(
      event.target
    );
  }
);